import { Component, DestroyRef, HostListener, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CalendarDateFormatter,
  CalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarView,
} from 'angular-calendar';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { AppointmentFormComponent } from '../appointment-form/appointment-form.component';
import { AppointmentService } from '../../services/projects.service';
import { BookingService } from '../../services/booking.service';
import { CustomDateFormatter } from '../../../../shared/customs/custom-date-formatter';
import { MasterService } from '../../../master/services/master.service';
import { UntypedFormControl } from '@angular/forms';
import { MasterModel } from '../../../master/models/master.model';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BookingActivationGuardService } from '../../services/booking-activation-guard.service';

interface DayTimelineMarker {
  top: number;
  label: string;
  isHour: boolean;
}

interface DayEventLayout {
  employeeId: string;
  event: CalendarEvent;
  top: number;
  height: number;
}

type DragMode = 'move' | 'resize-top' | 'resize-bottom';

interface DayDragState {
  mode: DragMode;
  event: CalendarEvent;
  originStart: Date;
  originEnd: Date;
  originEmployeeId: string;
  pointerStartY: number;
  pointerStartX: number;
  // Offset del puntero dentro de la tarjeta al iniciar el arrastre (para el fantasma)
  ghostOffsetX: number;
  ghostOffsetY: number;
  ghostWidth: number;
  // Preview values updated on mousemove
  previewStart: Date;
  previewEnd: Date;
  previewEmployeeId: string;
  moved: boolean;
}

interface WeekDragState {
  event: CalendarEvent;
  originStart: Date;
  originEnd: Date;
  originEmployeeId: string;
  originDayKey: string;
  pointerStartY: number;
  pointerStartX: number;
  ghostOffsetX: number;
  ghostOffsetY: number;
  ghostWidth: number;
  previewEmployeeId: string;
  previewDayKey: string;
  moved: boolean;
}

// Tarjeta flotante que sigue al cursor durante el arrastre.
interface DragGhost {
  x: number;
  y: number;
  width: number;
  time: string;
  contact: string;
  primary: string;
  secondary: string;
}

@Component({
    selector: 'app-booking-calendar',
    templateUrl: './booking-calendar.component.html',
    styleUrls: ['./booking-calendar.component.css'],
    providers: [
        { provide: LOCALE_ID, useValue: 'es' },
        { provide: CalendarDateFormatter, useClass: CustomDateFormatter },
        { provide: 'weekStartsOn', useValue: 1 },
    ],
    standalone: false
})
export class BookingCalendarComponent implements OnInit, OnDestroy {
  CalendarView = CalendarView;
  view: CalendarView = CalendarView.Week;
  viewDate: Date = new Date();

  selectedMonth = this.viewDate.getMonth();
  selectedYear = this.viewDate.getFullYear();

  months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  years = Array.from({ length: 10 }, (_, i) => this.selectedYear - 5 + i);

  events: CalendarEvent[] = [];
  eventsDtos = [];

  employeesControl = new UntypedFormControl([]);
  merchantLocationsControl = new UntypedFormControl([]);
  employees: MasterModel[] = [];
  merchantLocations: MasterModel[] = [];

  employeesConfig: any = {
    label: 'Empleados',
    placeholder: 'Filtrar por empleados...',
    options: [],
  };

  merchantLocationsConfig: any = {
    label: 'Centros de trabajo',
    placeholder: 'Filtrar por centros de trabajo...',
    options: [],
  };

  dayStartHour = 8;
  dayEndHour = 21;
  minuteHeight = 2;
  snapMinutes = 15;
  employeeColumnWidth = 240;
  timelineColumnWidth = 76;
  dayBodyHeight = 0;
  dayColumnsWidth = 240;
  visibleEmployees: MasterModel[] = [];
  timelineMarkers: DayTimelineMarker[] = [];
  hourLabels: DayTimelineMarker[] = [];
  eventLayoutsByEmployee: Record<string, DayEventLayout[]> = {};
  currentTimeIndicatorTop: number | null = null;

  weekDays: Date[] = [];
  weekEventsByEmployeeDay: Record<string, Record<string, CalendarEvent[]>> = {};
  monthVisibleEventsLimit = 3;
  expandedMonthDayKey: string | null = null;

  private readonly minimumEventHeight = 28;
  private readonly destroyRef = inject(DestroyRef);
  private readonly appointmentService = inject(AppointmentService);
  private readonly bookingService = inject(BookingService);
  private readonly masterService = inject(MasterService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly bookingActivationGuard = inject(BookingActivationGuardService);

  private readonly minimumDurationMinutes = 15;
  private readonly dragThresholdPx = 4;

  dayDragState: DayDragState | null = null;
  weekDragState: WeekDragState | null = null;
  dragGhost: DragGhost | null = null;
  isPersisting = false;

  ngOnInit(): void {
    this.bookingActivationGuard
      .verifyAndRedirectIfInactive()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === true) {
          this.initializeCalendar();
        }
      });
  }

  private initializeCalendar(): void {
    this.loadFilterOptions();
    const range = this.getRangeForCurrentView();
    this.loadAppointmentsByRange(range.from, range.to);
    this.rebuildDayView();

    this.employeesControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.rebuildDayView();
        const range = this.getRangeForCurrentView();
        this.loadAppointmentsByRange(range.from, range.to);
      });

    this.merchantLocationsControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadEmployeesForCurrentLocations();
        const range = this.getRangeForCurrentView();
        this.loadAppointmentsByRange(range.from, range.to);
      });
  }

  loadFilterOptions(): void {
    this.masterService
      .getMerchantLocations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locations) => {
        this.merchantLocations = locations;
        this.merchantLocationsConfig = {
          label: 'Centros de trabajo',
          placeholder: 'Filtrar por centros de trabajo...',
          options: locations,
        };

        this.loadEmployeesForCurrentLocations();
      });
  }

  private async loadEmployeesForCurrentLocations(): Promise<void> {
    const selectedLocations = this.merchantLocationsControl.value || [];

    if (selectedLocations.length === 0 && this.merchantLocations.length > 0) {
      const employeeRequests = this.merchantLocations.map((location) =>
        firstValueFrom(this.masterService.getEmployeesByLocationAndConcept(location.id))
      );

      const results = await Promise.all(employeeRequests);
      const employeeMap = new Map<string, MasterModel>();

      results.forEach((locationEmployees) => {
        if (locationEmployees) {
          locationEmployees.forEach((emp: MasterModel) => employeeMap.set(emp.id, emp));
        }
      });

      this.employees = Array.from(employeeMap.values());
      this.employeesConfig = {
        label: 'Personal',
        placeholder: 'Filtrar por personal...',
        options: this.employees,
      };
      this.rebuildDayView();
    } else if (selectedLocations.length > 0) {
      const employeeRequests = selectedLocations.map((location: MasterModel) =>
        firstValueFrom(this.masterService.getEmployeesByLocationAndConcept(location.id))
      );

      const results = await Promise.all(employeeRequests);
      const employeeMap = new Map<string, MasterModel>();

      results.forEach((locationEmployees) => {
        if (locationEmployees) {
          locationEmployees.forEach((emp: MasterModel) => employeeMap.set(emp.id, emp));
        }
      });

      this.employees = Array.from(employeeMap.values());
      this.employeesConfig = {
        label: 'Personal',
        placeholder: 'Filtrar por personal...',
        options: this.employees,
      };
      this.rebuildDayView();
    }
  }

  loadAppointmentsByRange(from: Date, to: Date): void {
    const filters: any = { from, to };

    const selectedEmployees = this.employeesControl.value || [];
    if (selectedEmployees.length > 0) {
      filters.employeeUids = selectedEmployees.map((e: MasterModel) => e.id);
    }

    const selectedLocations = this.merchantLocationsControl.value || [];
    if (selectedLocations.length > 0) {
      filters.merchantLocationUids = selectedLocations.map((l: MasterModel) => l.id);
    }

    this.appointmentService
      .getBySearch(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        const values = (response as any).values || [];
        const mappedEvents = values.map((e: any) => {
          const primaryColor = e.employeeCalendarColor || '#d48f00';
          const secondaryColor = this.hexToRgba(primaryColor, 1);

          const startDate = this.parseEventDate(e.from);
          const endDate = this.parseEventDate(e.to) || startDate;

          return {
            id: e.id,
            title: e.title,
            draggable: true,
            resizable: {
              beforeStart: true,
              afterEnd: true,
            },
            start: startDate,
            end: endDate,
            color: {
              primary: primaryColor,
              secondary: secondaryColor,
            },
            meta: { id: e.uid, data: e },
          };
        });

        this.events = mappedEvents.filter((event: CalendarEvent) => this.isDateValid(event.start));
        this.rebuildDayView();
      });
  }

  private parseEventDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    const parsedDate = new Date(value);
    return this.isDateValid(parsedDate) ? parsedDate : null;
  }

  private isDateValid(value: any): boolean {
    return value instanceof Date && !isNaN(value.getTime());
  }

  private hexToRgba(hex: string, alpha: number): string {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private rebuildDayView(): void {
    const totalMinutes = this.getDayTotalMinutes();

    this.dayBodyHeight = totalMinutes * this.minuteHeight;
    this.visibleEmployees = this.buildVisibleEmployees();
    this.dayColumnsWidth = Math.max(this.visibleEmployees.length, 1) * this.employeeColumnWidth;
    this.timelineMarkers = this.buildTimelineMarkers();
    this.hourLabels = this.timelineMarkers.filter((marker) => marker.isHour);
    this.currentTimeIndicatorTop = this.getCurrentTimeIndicatorTop();
    this.eventLayoutsByEmployee = this.buildEventLayoutsByEmployee();
    this.buildWeekView();
  }

  private buildWeekView(): void {
    this.weekDays = this.buildWeekDays();
    this.weekEventsByEmployeeDay = this.buildWeekEventsByEmployeeDay();
  }

  private buildWeekDays(): Date[] {
    const start = startOfWeek(this.viewDate, { weekStartsOn: 1 });
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    return days;
  }

  private buildWeekEventsByEmployeeDay(): Record<string, Record<string, CalendarEvent[]>> {
    const result: Record<string, Record<string, CalendarEvent[]>> = {};

    this.visibleEmployees.forEach((employee) => {
      result[employee.id] = {};
      this.weekDays.forEach((day) => {
        result[employee.id][this.getWeekDayKey(day)] = [];
      });
    });

    this.events.forEach((event) => {
      const employee = this.resolveEmployeeFromEvent(event);
      if (!employee || !result[employee.id]) {
        return;
      }
      const dayKey = this.getWeekDayKey(event.start);
      if (!result[employee.id][dayKey]) {
        result[employee.id][dayKey] = [];
      }
      result[employee.id][dayKey].push(event);
    });

    Object.keys(result).forEach((empId) => {
      Object.keys(result[empId]).forEach((dayKey) => {
        result[empId][dayKey].sort((a, b) => a.start.getTime() - b.start.getTime());
      });
    });

    return result;
  }

  getWeekDayKey(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }

  isWeekDayToday(date: Date): boolean {
    return isToday(date);
  }

  getWeekDayEvents(employeeId: string, day: Date): CalendarEvent[] {
    const byEmployee = this.weekEventsByEmployeeDay[employeeId];
    if (!byEmployee) {
      return [];
    }
    return byEmployee[this.getWeekDayKey(day)] || [];
  }

  formatWeekDayName(day: Date): string {
    return format(day, 'EEE', { locale: es }).toUpperCase();
  }

  formatWeekDayNum(day: Date): string {
    return format(day, 'd');
  }

  onWeekCellClick(day: Date, employee: MasterModel): void {
    const clickDate = new Date(day);
    clickDate.setHours(9, 0, 0, 0);
    this.onHourClicked({ date: clickDate }, employee);
  }

  getMonthVisibleEvents(day: any): CalendarEvent[] {
    const sorted = this.getMonthAllEvents(day);
    return sorted.slice(0, this.monthVisibleEventsLimit);
  }

  getMonthHiddenEventsCount(day: any): number {
    const total = this.getMonthAllEvents(day).length;
    return Math.max(total - this.monthVisibleEventsLimit, 0);
  }

  getMonthHiddenEvents(day: any): CalendarEvent[] {
    const sorted = this.getMonthAllEvents(day);
    return sorted.slice(this.monthVisibleEventsLimit);
  }

  isMonthDayExpanded(day: any): boolean {
    return this.expandedMonthDayKey === this.getMonthDayKey(day);
  }

  toggleMonthDayExpanded(day: any, mouseEvent: MouseEvent): void {
    mouseEvent.stopPropagation();
    const dayKey = this.getMonthDayKey(day);
    this.expandedMonthDayKey = this.expandedMonthDayKey === dayKey ? null : dayKey;
  }

  private getMonthAllEvents(day: any): CalendarEvent[] {
    const dayEvents = day && day.events ? (day.events as CalendarEvent[]) : [];
    return dayEvents.slice().sort((left, right) => left.start.getTime() - right.start.getTime());
  }

  private getMonthDayKey(day: any): string {
    const dayDate = day && day.date ? day.date : new Date();
    return format(dayDate, 'yyyy-MM-dd');
  }

  formatMonthEventStart(event: CalendarEvent): string {
    if (!this.isDateValid(event && event.start)) {
      return '--:--';
    }

    return format(event.start, 'HH:mm', { locale: es });
  }

  trackByDay(_: number, day: Date): string {
    return format(day, 'yyyy-MM-dd');
  }

  private buildVisibleEmployees(): MasterModel[] {
    const selectedEmployees = this.getSelectedEmployees();
    const selectedEmployeeIds = new Set(selectedEmployees.map((employee) => employee.id));
    const employeeMap = new Map<string, MasterModel>();

    const baseEmployees = selectedEmployees.length > 0 ? selectedEmployees : this.employees;
    baseEmployees.forEach((employee) => employeeMap.set(employee.id, employee));

    if (selectedEmployees.length === 0) {
      this.events.forEach((event) => {
        const employee = this.resolveEmployeeFromEvent(event);

        if (employee && !employeeMap.has(employee.id)) {
          employeeMap.set(employee.id, employee);
        }
      });
    }

    if (selectedEmployees.length > 0) {
      return Array.from(employeeMap.values()).filter((employee) =>
        selectedEmployeeIds.has(employee.id)
      );
    }

    return Array.from(employeeMap.values());
  }

  private buildTimelineMarkers(): DayTimelineMarker[] {
    const markers: DayTimelineMarker[] = [];

    for (let minutes = 0; minutes <= this.getDayTotalMinutes(); minutes += 30) {
      const absoluteMinutes = this.dayStartHour * 60 + minutes;
      const hours = Math.floor(absoluteMinutes / 60);
      const mins = absoluteMinutes % 60;

      markers.push({
        top: minutes * this.minuteHeight,
        label: `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`,
        isHour: mins === 0,
      });
    }

    return markers;
  }

  private buildEventLayoutsByEmployee(): Record<string, DayEventLayout[]> {
    const layouts: Record<string, DayEventLayout[]> = {};

    this.visibleEmployees.forEach((employee) => {
      layouts[employee.id] = [];
    });

    this.events.forEach((event) => {
      const employee = this.resolveEmployeeFromEvent(event);

      if (!employee || !layouts[employee.id]) {
        return;
      }

      const layout = this.createDayEventLayout(event, employee.id);

      if (!layout) {
        return;
      }

      layouts[employee.id].push(layout);
    });

    Object.keys(layouts).forEach((employeeId) => {
      layouts[employeeId] = layouts[employeeId].sort((left, right) => left.top - right.top);
    });

    return layouts;
  }

  private createDayEventLayout(event: CalendarEvent, employeeId: string): DayEventLayout | null {
    const dayRangeStart = this.getDayRangeStart(this.viewDate);
    const dayRangeEnd = this.getDayRangeEnd(this.viewDate);
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end || event.start);

    if (eventEnd <= dayRangeStart || eventStart >= dayRangeEnd) {
      return null;
    }

    const clampedStart = eventStart < dayRangeStart ? dayRangeStart : eventStart;
    const clampedEnd = eventEnd > dayRangeEnd ? dayRangeEnd : eventEnd;
    const startMinutes = Math.max(
      0,
      Math.floor((clampedStart.getTime() - dayRangeStart.getTime()) / 60000)
    );
    const durationMinutes = Math.max(
      15,
      Math.ceil((clampedEnd.getTime() - clampedStart.getTime()) / 60000)
    );

    return {
      employeeId,
      event,
      top: startMinutes * this.minuteHeight,
      height: Math.max(durationMinutes * this.minuteHeight, this.minimumEventHeight),
    };
  }

  private getCurrentTimeIndicatorTop(): number | null {
    if (!isToday(this.viewDate)) {
      return null;
    }

    const now = new Date();
    const rangeStart = this.getDayRangeStart(this.viewDate);
    const rangeEnd = this.getDayRangeEnd(this.viewDate);

    if (now < rangeStart || now > rangeEnd) {
      return null;
    }

    const minutes = (now.getTime() - rangeStart.getTime()) / 60000;
    return minutes * this.minuteHeight;
  }

  private getDayTotalMinutes(): number {
    return Math.max((this.dayEndHour - this.dayStartHour) * 60, 0);
  }

  private getSelectedEmployees(): MasterModel[] {
    return (this.employeesControl.value || []) as MasterModel[];
  }

  private resolveEmployeeFromEvent(event: CalendarEvent): MasterModel | null {
    const employeeId = this.getEmployeeUidFromEvent(event);

    if (employeeId) {
      return this.findEmployeeById(employeeId) || {
        id: employeeId,
        display: this.getEmployeeNameFromEvent(event) || 'Empleado',
      };
    }

    const employeeName = this.getEmployeeNameFromEvent(event);

    if (!employeeName) {
      return null;
    }

    return this.findEmployeeByName(employeeName) || {
      id: `employee-${this.slugify(employeeName)}`,
      display: employeeName,
    };
  }

  getEmployeeUidFromEvent(event: CalendarEvent): string | null {
    const data = event && event.meta ? event.meta.data || {} : {};
    const employeeData = data.employee || {};
    const staffData = data.staff || {};
    const candidates = [
      data.employeeUid,
      data.employeeId,
      data.merchantStaffId,
      data.merchantStaffUid,
      data.staffId,
      data.staffUid,
      employeeData.uid,
      employeeData.id,
      staffData.uid,
      staffData.id,
    ];

    for (const candidate of candidates) {
      const normalizedCandidate = this.normalizeIdentifier(candidate);

      if (normalizedCandidate) {
        return normalizedCandidate;
      }
    }

    const employeeName = this.getEmployeeNameFromEvent(event);

    if (!employeeName) {
      return null;
    }

    const employee = this.findEmployeeByName(employeeName);
    return employee ? employee.id : null;
  }

  private getEmployeeNameFromEvent(event: CalendarEvent): string {
    const data = event && event.meta ? event.meta.data || {} : {};
    const employeeData = data.employee || {};
    const staffData = data.staff || {};
    const candidates = [
      data.employeeName,
      data.staffName,
      employeeData.name,
      employeeData.display,
      staffData.name,
      staffData.display,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }

    return '';
  }

  private normalizeIdentifier(value: any): string | null {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }

    if (typeof value === 'number') {
      return value.toString();
    }

    return null;
  }

  private findEmployeeById(employeeId: string): MasterModel | null {
    return this.employees.find((employee) => employee.id === employeeId) || null;
  }

  private findEmployeeByName(employeeName: string): MasterModel | null {
    const normalizedName = this.normalizeText(employeeName);
    return (
      this.employees.find((employee) => this.normalizeText(employee.display) === normalizedName) ||
      null
    );
  }

  private normalizeText(value: string): string {
    return (value || '').trim().toLowerCase();
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  getEmployeeAvatarUrl(employee: MasterModel): string | null {
    const metadata = employee && employee.metadata ? employee.metadata : {};
    const avatarCandidate =
      metadata.avatar ||
      metadata.avatarUrl ||
      metadata.photo ||
      metadata.photoUrl ||
      metadata.image ||
      metadata.imageUrl;
    return typeof avatarCandidate === 'string' && avatarCandidate.trim().length > 0
      ? avatarCandidate
      : null;
  }

  getEmployeeInitials(employee: MasterModel): string {
    const source = ((employee && employee.display) || 'Empleado').trim();
    return source
      .split(' ')
      .filter((part) => part.length > 0)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('');
  }

  getEventContactName(event: CalendarEvent): string {
    const data = event && event.meta ? event.meta.data || {} : {};
    return data.contactName || event.title || 'Cita';
  }

  getEventServiceName(event: CalendarEvent): string {
    const data = event && event.meta ? event.meta.data || {} : {};
    return data.conceptName || this.getEmployeeNameFromEvent(event) || 'Servicio';
  }

  formatEventTimeRange(event: CalendarEvent): string {
    const startDate = event ? event.start : null;
    const endDate = event && event.end ? event.end : startDate;

    if (!this.isDateValid(startDate)) {
      return '--:-- - --:--';
    }

    const safeEndDate = this.isDateValid(endDate) ? endDate : startDate;
    const start = format(startDate, 'HH:mm', { locale: es });
    const end = format(safeEndDate, 'HH:mm', { locale: es });
    return `${start} - ${end}`;
  }

  getEventAriaLabel(event: CalendarEvent): string {
    return `${this.formatEventTimeRange(event)}. ${this.getEventContactName(
      event
    )}. ${this.getEventServiceName(event)}.`;
  }

  onDayColumnClick(mouseEvent: MouseEvent, employee: MasterModel): void {
    const selectedDate = this.getDateFromColumnClick(mouseEvent);
    this.onHourClicked({ date: selectedDate }, employee);
  }

  onDayEventKeydown(keyboardEvent: KeyboardEvent, event: CalendarEvent): void {
    if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
      keyboardEvent.preventDefault();
      this.onEventClicked({ event });
    }
  }

  private getDateFromColumnClick(mouseEvent: MouseEvent): Date {
    const target = mouseEvent.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const offsetY = Math.max(0, Math.min(mouseEvent.clientY - rect.top, this.dayBodyHeight));
    const snappedMinutes =
      Math.round(offsetY / this.minuteHeight / this.snapMinutes) * this.snapMinutes;

    return this.getSnappedDate(this.viewDate, snappedMinutes);
  }

  private getSnappedDate(baseDate: Date, minutesFromDayStart: number): Date {
    const snappedDate = new Date(baseDate);
    snappedDate.setHours(this.dayStartHour, 0, 0, 0);
    snappedDate.setMinutes(snappedDate.getMinutes() + minutesFromDayStart);
    return snappedDate;
  }

  private getDayRangeStart(date: Date): Date {
    const rangeStart = new Date(date);
    rangeStart.setHours(this.dayStartHour, 0, 0, 0);
    return rangeStart;
  }

  private getDayRangeEnd(date: Date): Date {
    const rangeEnd = new Date(date);
    rangeEnd.setHours(this.dayEndHour, 0, 0, 0);
    return rangeEnd;
  }

  trackByEmployee(_: number, employee: MasterModel): string {
    return employee.id;
  }

  trackByMarker(_: number, marker: DayTimelineMarker): string {
    return `${marker.label}-${marker.top}`;
  }

  trackByEventLayout(_: number, layout: DayEventLayout): string | number {
    return (
      layout.event.id ||
      (layout.event.meta ? layout.event.meta.id : null) ||
      `${layout.employeeId}-${layout.top}`
    );
  }

  get viewTitle(): string {
    if (this.view === CalendarView.Month) {
      return format(this.viewDate, 'MMMM yyyy', { locale: es });
    }
    if (this.view === CalendarView.Week) {
      const start = startOfWeek(this.viewDate, { weekStartsOn: 1 });
      const end = endOfWeek(this.viewDate, { weekStartsOn: 1 });
      return `Semana del ${format(start, "d 'al' ", { locale: es })}${format(
        end,
        "d 'de' MMMM yyyy",
        { locale: es }
      )}`;
    }
    if (this.view === CalendarView.Day) {
      return format(this.viewDate, "EEEE d 'de' MMMM yyyy", { locale: es });
    }
    return '';
  }

  private getRangeForCurrentView(): { from: Date; to: Date } {
    let from: Date;
    let to: Date;

    if (this.view === CalendarView.Day) {
      from = new Date(this.viewDate);
      from.setHours(0, 0, 0, 0);
      to = new Date(this.viewDate);
      to.setHours(23, 59, 59, 999);
    } else if (this.view === CalendarView.Week) {
      from = startOfWeek(this.viewDate, { weekStartsOn: 1 });
      to = endOfWeek(this.viewDate, { weekStartsOn: 1 });
    } else {
      from = startOfMonth(this.viewDate);
      to = endOfMonth(this.viewDate);
    }

    return { from, to };
  }

  previous(): void {
    if (this.view === CalendarView.Month) {
      this.viewDate = addMonths(this.viewDate, -1);
    } else {
      this.viewDate = addDays(this.viewDate, this.view === CalendarView.Week ? -7 : -1);
    }
    this.syncSelectors();
    this.rebuildDayView();
    const range = this.getRangeForCurrentView();
    this.loadAppointmentsByRange(range.from, range.to);
  }

  next(): void {
    if (this.view === CalendarView.Month) {
      this.viewDate = addMonths(this.viewDate, 1);
    } else {
      this.viewDate = addDays(this.viewDate, this.view === CalendarView.Week ? 7 : 1);
    }
    this.syncSelectors();
    this.rebuildDayView();
    const range = this.getRangeForCurrentView();
    this.loadAppointmentsByRange(range.from, range.to);
  }

  today(): void {
    this.viewDate = new Date();
    this.syncSelectors();
    this.rebuildDayView();
    const range = this.getRangeForCurrentView();
    this.loadAppointmentsByRange(range.from, range.to);
  }

  onViewChange(view: CalendarView): void {
    this.view = view;
    this.rebuildDayView();
    const range = this.getRangeForCurrentView();
    this.loadAppointmentsByRange(range.from, range.to);
  }

  changeMonth(): void {
    const newDate = new Date(this.viewDate);
    newDate.setMonth(this.selectedMonth);
    this.viewDate = newDate;
    this.rebuildDayView();

    const range = this.getRangeForCurrentView();
    this.loadAppointmentsByRange(range.from, range.to);
  }

  changeYear(): void {
    const newDate = new Date(this.viewDate);
    newDate.setFullYear(this.selectedYear);
    this.viewDate = newDate;
    this.rebuildDayView();

    const range = this.getRangeForCurrentView();
    this.loadAppointmentsByRange(range.from, range.to);
  }

  private syncSelectors(): void {
    this.selectedMonth = this.viewDate.getMonth();
    this.selectedYear = this.viewDate.getFullYear();
  }

  onEventClicked({ event }: { event: CalendarEvent }): void {
    this.dialog.closeAll();
    const dialogRef = this.dialog.open(AppointmentFormComponent, {
      width: '400px',
      maxWidth: '100vw',
      height: '100dvh',
      position: { right: '0', top: '0' },
      hasBackdrop: false,
      panelClass: 'side-dialog-panel',
      data: {
        mode: 'panel',
        formValue: {
          uid: event.meta.id,
          type: event.meta.data.type,
        },
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      const range = this.getRangeForCurrentView();
      this.loadAppointmentsByRange(range.from, range.to);
    });
  }

  onHourClicked({ date }: { date: Date }, employee?: MasterModel): void {
    this.dialog.closeAll();
    const selectedDate = new Date(date);

    const endDate = new Date(selectedDate);
    endDate.setHours(selectedDate.getHours() + 1);

    const pad = (n: number) => (n < 10 ? '0' + n : n);
    const formatTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

    const dialogRef = this.dialog.open(AppointmentFormComponent, {
      width: '400px',
      maxWidth: '100vw',
      height: '100dvh',
      position: { right: '0', top: '0' },
      hasBackdrop: false,
      panelClass: 'side-dialog-panel',
      data: {
        mode: 'panel',
        formValue: {
          date: selectedDate,
          startTime: formatTime(selectedDate),
          endTime: formatTime(endDate),
          employeeUid: employee ? { id: employee.id, display: employee.display } : null,
        },
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      const range = this.getRangeForCurrentView();
      this.loadAppointmentsByRange(range.from, range.to);
    });
  }

  onDayClicked({ date }: { date: Date }): void {
    // Click en cabecera de día (vista Mes): abrir el panel de nueva cita
    // a las 9:00 del día seleccionado, sin empleado preseleccionado.
    const start = new Date(date);
    start.setHours(9, 0, 0, 0);
    this.onHourClicked({ date: start });
  }

  /**
   * Disparado por mwl-calendar-month-view al hacer drag entre celdas.
   * Solo cambia la fecha (preserva la hora original).
   */
  onEventTimesChanged({ event, newStart }: CalendarEventTimesChangedEvent): void {
    if (!this.isDateValid(event && event.start) || !this.isDateValid(newStart)) {
      return;
    }
    const originStart = new Date(event.start);
    const originEnd = this.isDateValid(event.end) ? new Date(event.end) : originStart;
    const durationMs = originEnd.getTime() - originStart.getTime();

    const targetStart = new Date(newStart);
    targetStart.setHours(originStart.getHours(), originStart.getMinutes(), 0, 0);
    const targetEnd = new Date(targetStart.getTime() + durationMs);

    this.persistEventTimes(event, targetStart, targetEnd);
  }

  // ============================================================
  // Drag & resize — vista Día
  // ============================================================

  onDayEventMouseDown(mouseEvent: MouseEvent, layout: DayEventLayout, mode: DragMode): void {
    if (mouseEvent.button !== 0 || this.isPersisting) { return; }
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    const start = new Date(layout.event.start);
    const end = layout.event.end ? new Date(layout.event.end) : new Date(start.getTime() + 60 * 60 * 1000);
    const rect = (mouseEvent.currentTarget as HTMLElement).getBoundingClientRect();

    this.dayDragState = {
      mode,
      event: layout.event,
      originStart: start,
      originEnd: end,
      originEmployeeId: layout.employeeId,
      pointerStartY: mouseEvent.clientY,
      pointerStartX: mouseEvent.clientX,
      ghostOffsetX: mouseEvent.clientX - rect.left,
      ghostOffsetY: mouseEvent.clientY - rect.top,
      ghostWidth: rect.width,
      previewStart: start,
      previewEnd: end,
      previewEmployeeId: layout.employeeId,
      moved: false
    };
  }

  onWeekEventMouseDown(mouseEvent: MouseEvent, event: CalendarEvent, employeeId: string, day: Date): void {
    if (mouseEvent.button !== 0 || this.isPersisting) { return; }
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    const start = new Date(event.start);
    const end = event.end ? new Date(event.end) : new Date(start.getTime() + 60 * 60 * 1000);
    const dayKey = this.getWeekDayKey(day);
    const rect = (mouseEvent.currentTarget as HTMLElement).getBoundingClientRect();

    this.weekDragState = {
      event,
      originStart: start,
      originEnd: end,
      originEmployeeId: employeeId,
      originDayKey: dayKey,
      pointerStartY: mouseEvent.clientY,
      pointerStartX: mouseEvent.clientX,
      ghostOffsetX: mouseEvent.clientX - rect.left,
      ghostOffsetY: mouseEvent.clientY - rect.top,
      ghostWidth: rect.width,
      previewEmployeeId: employeeId,
      previewDayKey: dayKey,
      moved: false
    };
  }

  // Pointer Events (no mouse): así funciona igual con ratón, dedo (móvil/tablet)
  // y lápiz. PointerEvent extiende MouseEvent, por lo que clientX/clientY/button
  // siguen disponibles en los métodos de preview/commit.
  @HostListener('document:pointermove', ['$event'])
  onDocumentMouseMove(mouseEvent: MouseEvent): void {
    if (this.dayDragState) {
      this.updateDayDragPreview(mouseEvent);
    } else if (this.weekDragState) {
      this.updateWeekDragPreview(mouseEvent);
    }
  }

  @HostListener('document:pointerup', ['$event'])
  onDocumentMouseUp(mouseEvent: MouseEvent): void {
    if (this.dayDragState) {
      this.commitDayDrag(mouseEvent);
    } else if (this.weekDragState) {
      this.commitWeekDrag(mouseEvent);
    }
  }

  // En táctil, si el navegador cancela el gesto (p. ej. empieza a hacer scroll)
  // llega pointercancel: abortamos el arrastre sin persistir y revertimos.
  @HostListener('document:pointercancel')
  onDocumentPointerCancel(): void {
    if (this.dayDragState) {
      const state = this.dayDragState;
      this.dayDragState = null;
      this.dragGhost = null;
      if (state.moved) {
        state.event.start = state.originStart;
        state.event.end = state.originEnd;
        this.eventLayoutsByEmployee = this.buildEventLayoutsByEmployee();
      }
    }
    this.weekDragState = null;
    this.dragGhost = null;
  }

  private updateDayDragPreview(mouseEvent: MouseEvent): void {
    const state = this.dayDragState!;
    const deltaY = mouseEvent.clientY - state.pointerStartY;
    const deltaX = mouseEvent.clientX - state.pointerStartX;

    if (!state.moved && Math.abs(deltaY) < this.dragThresholdPx && Math.abs(deltaX) < this.dragThresholdPx) {
      return;
    }
    state.moved = true;

    const deltaMinutes = this.snapToInterval(deltaY / this.minuteHeight);

    if (state.mode === 'move') {
      const durationMs = state.originEnd.getTime() - state.originStart.getTime();
      const tentativeStart = this.addMinutes(state.originStart, deltaMinutes);
      const clamped = this.clampStartToDay(tentativeStart, durationMs);
      state.previewStart = clamped.start;
      state.previewEnd = new Date(clamped.start.getTime() + durationMs);
      state.previewEmployeeId = this.findEmployeeIdAtX(mouseEvent.clientX) || state.originEmployeeId;
    } else if (state.mode === 'resize-top') {
      const tentativeStart = this.addMinutes(state.originStart, deltaMinutes);
      const maxStart = this.addMinutes(state.originEnd, -this.minimumDurationMinutes);
      const minStart = this.getDayRangeStart(this.viewDate);
      const clamped = new Date(Math.max(minStart.getTime(), Math.min(maxStart.getTime(), tentativeStart.getTime())));
      state.previewStart = clamped;
      state.previewEnd = state.originEnd;
    } else {
      const tentativeEnd = this.addMinutes(state.originEnd, deltaMinutes);
      const minEnd = this.addMinutes(state.originStart, this.minimumDurationMinutes);
      const maxEnd = this.getDayRangeEnd(this.viewDate);
      const clamped = new Date(Math.max(minEnd.getTime(), Math.min(maxEnd.getTime(), tentativeEnd.getTime())));
      state.previewStart = state.originStart;
      state.previewEnd = clamped;
    }

    // Update the event in-place so the existing layout pipeline re-renders the preview
    state.event.start = state.previewStart;
    state.event.end = state.previewEnd;
    this.eventLayoutsByEmployee = this.buildEventLayoutsByEmployee();

    // El fantasma solo tiene sentido al mover (en resize el borde ya sigue al cursor).
    if (state.mode === 'move') {
      this.setDragGhost(state.event, mouseEvent, state.ghostOffsetX, state.ghostOffsetY, state.ghostWidth);
    }
  }

  private commitDayDrag(_mouseEvent: MouseEvent): void {
    const state = this.dayDragState!;
    this.dayDragState = null;
    this.dragGhost = null;

    if (!state.moved) { return; }

    const changedEmployee = state.previewEmployeeId !== state.originEmployeeId;
    const changedTime = state.previewStart.getTime() !== state.originStart.getTime()
                     || state.previewEnd.getTime() !== state.originEnd.getTime();

    if (!changedEmployee && !changedTime) { return; }

    const targetEmployeeUid = changedEmployee && this.canReassignEmployee(state.event)
      ? state.previewEmployeeId
      : undefined;

    this.persistEventTimes(state.event, state.previewStart, state.previewEnd, targetEmployeeUid, () => {
      // Revert preview on failure
      state.event.start = state.originStart;
      state.event.end = state.originEnd;
      this.eventLayoutsByEmployee = this.buildEventLayoutsByEmployee();
    });
  }

  private updateWeekDragPreview(mouseEvent: MouseEvent): void {
    const state = this.weekDragState!;
    const deltaY = mouseEvent.clientY - state.pointerStartY;
    const deltaX = mouseEvent.clientX - state.pointerStartX;

    if (!state.moved && Math.abs(deltaY) < this.dragThresholdPx && Math.abs(deltaX) < this.dragThresholdPx) {
      return;
    }
    state.moved = true;

    const target = this.findWeekCellAt(mouseEvent.clientX, mouseEvent.clientY);
    if (target) {
      state.previewEmployeeId = target.employeeId;
      state.previewDayKey = target.dayKey;
    }

    this.setDragGhost(state.event, mouseEvent, state.ghostOffsetX, state.ghostOffsetY, state.ghostWidth);
  }

  private commitWeekDrag(_mouseEvent: MouseEvent): void {
    const state = this.weekDragState!;
    this.weekDragState = null;
    this.dragGhost = null;

    if (!state.moved) { return; }

    const changedEmployee = state.previewEmployeeId !== state.originEmployeeId;
    const changedDay = state.previewDayKey !== state.originDayKey;

    if (!changedEmployee && !changedDay) { return; }

    const targetDay = this.weekDays.find(d => this.getWeekDayKey(d) === state.previewDayKey);
    if (!targetDay) { return; }

    const newStart = new Date(targetDay);
    newStart.setHours(state.originStart.getHours(), state.originStart.getMinutes(), 0, 0);
    const durationMs = state.originEnd.getTime() - state.originStart.getTime();
    const newEnd = new Date(newStart.getTime() + durationMs);

    const targetEmployeeUid = changedEmployee && this.canReassignEmployee(state.event)
      ? state.previewEmployeeId
      : undefined;

    this.persistEventTimes(state.event, newStart, newEnd, targetEmployeeUid);
  }

  /**
   * Coloca/actualiza la tarjeta flotante que sigue al cursor durante el arrastre.
   * Se posiciona en coordenadas de viewport (position: fixed), restando el offset
   * que el puntero tenía dentro de la tarjeta al iniciar, para que no "salte".
   */
  private setDragGhost(
    event: CalendarEvent,
    mouseEvent: MouseEvent,
    offsetX: number,
    offsetY: number,
    width: number
  ): void {
    this.dragGhost = {
      x: mouseEvent.clientX - offsetX,
      y: mouseEvent.clientY - offsetY,
      width,
      time: this.formatEventTimeRange(event),
      contact: this.getEventContactName(event),
      primary: (event.color && event.color.primary) || '#d48f00',
      secondary: (event.color && event.color.secondary) || '#d48f00'
    };
  }

  private findEmployeeIdAtX(clientX: number): string | null {
    const columns = document.querySelectorAll('.booksy-employee-column');
    for (let i = 0; i < columns.length && i < this.visibleEmployees.length; i++) {
      const rect = (columns[i] as HTMLElement).getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        return this.visibleEmployees[i].id;
      }
    }
    return null;
  }

  private findWeekCellAt(clientX: number, clientY: number): { employeeId: string; dayKey: string } | null {
    const target = document.elementFromPoint(clientX, clientY) as Element | null;
    if (!target) { return null; }
    const cell = target.closest('.booksy-week-day-cell') as HTMLElement | null;
    if (!cell) { return null; }
    const employeeId = cell.getAttribute('data-employee-id');
    const dayKey = cell.getAttribute('data-day-key');
    if (!employeeId || !dayKey) { return null; }
    return { employeeId, dayKey };
  }

  private snapToInterval(minutes: number): number {
    return Math.round(minutes / this.snapMinutes) * this.snapMinutes;
  }

  private addMinutes(date: Date, minutes: number): Date {
    const result = new Date(date);
    result.setMinutes(result.getMinutes() + minutes);
    return result;
  }

  private clampStartToDay(start: Date, durationMs: number): { start: Date } {
    const dayStart = this.getDayRangeStart(start);
    const dayEnd = this.getDayRangeEnd(start);
    const maxStartTime = dayEnd.getTime() - durationMs;
    const clamped = Math.max(dayStart.getTime(), Math.min(maxStartTime, start.getTime()));
    return { start: new Date(clamped) };
  }

  // ============================================================
  // Persistencia: enruta según tipo de cita (appointfactu vs booking)
  // ============================================================

  private persistEventTimes(
    event: CalendarEvent,
    newStart: Date,
    newEnd: Date,
    newEmployeeUid?: string,
    onError?: () => void
  ): void {
    const data = (event && event.meta && event.meta.data) || {};
    const type = data.type || 'appointfactu';
    const uid = (event.meta && event.meta.id) || data.uid;

    if (!uid) {
      this.handlePersistError('Cita sin identificador. No se puede mover.', onError);
      return;
    }

    this.isPersisting = true;

    if (type === 'booking') {
      this.persistBooking(uid, newStart, newEnd, newEmployeeUid, onError);
    } else {
      this.persistAppointfactu(uid, newStart, newEnd, newEmployeeUid, onError);
    }
  }

  private persistAppointfactu(
    uid: string,
    newStart: Date,
    newEnd: Date,
    newEmployeeUid: string | undefined,
    onError?: () => void
  ): void {
    const payload = {
      date: this.toIsoDate(newStart),
      startTime: this.toTimeOfDay(newStart),
      endTime: this.toTimeOfDay(newEnd),
      employeeUid: newEmployeeUid
    };

    this.appointmentService.reschedule(uid, payload).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.isPersisting = false;
        if (response && response.isSuccess) {
          this.showInfo('Cita actualizada');
          this.refreshCurrentRange();
        } else {
          const message = (response && response.error && response.error.message) || 'No se pudo actualizar la cita';
          this.handlePersistError(message, onError);
        }
      },
      error: () => this.handlePersistError('Error de red al actualizar la cita', onError)
    });
  }

  private persistBooking(
    bookingId: string,
    newStart: Date,
    newEnd: Date,
    newEmployeeUid: string | undefined,
    onError?: () => void
  ): void {
    // El backend de booking recalcula EndAt según la duración del concepto,
    // así que mandamos endAt como hint y dejamos que decida. Necesitamos GET
    // para preservar customer/notes/concept (el PUT los sobrescribe si faltan).
    this.bookingService.getById(bookingId).pipe(take(1)).subscribe({
      next: (response: any) => {
        const booking = response && response.value;
        if (!booking) {
          this.handlePersistError('No se pudo cargar la reserva', onError);
          return;
        }

        const update: any = {
          bookingId,
          merchantLocationConceptId: booking.merchantLocationConceptId,
          merchantStaffId: newEmployeeUid || booking.merchantStaffId,
          startAt: this.toIsoLocal(newStart),
          endAt: this.toIsoLocal(newEnd),
          customerName: booking.customerName || '',
          customerEmail: booking.customerEmail || null,
          customerPhone: booking.customerPhone || null,
          notes: booking.notes || ''
        };

        this.bookingService.update(update).pipe(take(1)).subscribe({
          next: (updateResponse: any) => {
            this.isPersisting = false;
            if (updateResponse && updateResponse.isSuccess) {
              this.showInfo('Reserva actualizada');
              this.refreshCurrentRange();
            } else {
              const message = (updateResponse && updateResponse.error && updateResponse.error.message) || 'No se pudo actualizar la reserva';
              this.handlePersistError(message, onError);
            }
          },
          error: () => this.handlePersistError('Error de red al actualizar la reserva', onError)
        });
      },
      error: () => this.handlePersistError('No se pudo cargar la reserva', onError)
    });
  }

  private refreshCurrentRange(): void {
    const range = this.getRangeForCurrentView();
    this.loadAppointmentsByRange(range.from, range.to);
  }

  private handlePersistError(message: string, onError?: () => void): void {
    this.isPersisting = false;
    if (onError) { onError(); }
    this.snackBar.open(message, 'Cerrar', { duration: 4000, panelClass: 'snack-error' });
  }

  private showInfo(message: string): void {
    this.snackBar.open(message, 'Cerrar', { duration: 2500 });
  }

  /**
   * Solo se puede reasignar empleado si el evento trae un UID de empleado
   * reconocible (no un placeholder construido a partir del nombre).
   */
  private canReassignEmployee(event: CalendarEvent): boolean {
    const uid = this.getEmployeeUidFromEvent(event);
    return !!uid && !uid.startsWith('employee-');
  }

  private toIsoDate(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }

  private toTimeOfDay(date: Date): string {
    return format(date, 'HH:mm:ss');
  }

  private toIsoLocal(date: Date): string {
    // Local ISO sin offset (yyyy-MM-ddTHH:mm:ss) para evitar el corrimiento
    // por zona horaria al deserializar en el backend.
    return format(date, "yyyy-MM-dd'T'HH:mm:ss");
  }

  ngOnDestroy(): void {
    this.dialog.closeAll();
  }
  public trackByIndex(i: number) { return i; }

}